import type { CachedAppPageValue, CacheControlMetadata } from "vinext/shims/cache";
import { VINEXT_RSC_VARY_HEADER } from "./app-rsc-cache-busting.js";
import { buildCachedRevalidateCacheControl } from "./cache-control.js";
import { VINEXT_CACHE_HEADER, VINEXT_MOUNTED_SLOTS_HEADER } from "./headers.js";
import { buildAppPageCacheValue, type ISRCacheEntry } from "./isr-cache.js";
import { mergeMiddlewareResponseHeaders } from "./middleware-response-headers.js";
import { readStreamAsText } from "../utils/text-stream.js";
import { encodeCacheTag } from "../utils/encode-cache-tag.js";
import type { AppRscRenderMode } from "./app-rsc-render-mode.js";

type AppPageDebugLogger = (event: string, detail: string) => void;
type AppPageCacheGetter = (key: string) => Promise<ISRCacheEntry | null>;
type AppPageCacheSetter = (
  key: string,
  data: CachedAppPageValue,
  revalidateSeconds: number,
  tags: string[],
  expireSeconds?: number,
) => Promise<void>;
type AppPageBackgroundRegenerator = (key: string, renderFn: () => Promise<void>) => void;
type AppPageRequestCacheLife = {
  revalidate?: number;
  expire?: number;
};

type AppPageCacheRenderResult = {
  cacheControl?: CacheControlMetadata;
  html: string;
  rscData: ArrayBuffer;
  tags: string[];
};

type BuildAppPageCachedResponseOptions = {
  cacheControl?: CacheControlMetadata;
  cacheState: "HIT" | "STALE";
  expireSeconds?: number;
  isRscRequest: boolean;
  middlewareHeaders?: Headers | null;
  middlewareStatus?: number | null;
  mountedSlotsHeader?: string | null;
  revalidateSeconds: number;
};

type ReadAppPageCacheResponseOptions = {
  cleanPathname: string;
  clearRequestContext: () => void;
  isRscRequest: boolean;
  isrDebug?: AppPageDebugLogger;
  isrGet: AppPageCacheGetter;
  isrHtmlKey: (pathname: string) => string;
  isrRscKey: (
    pathname: string,
    mountedSlotsHeader?: string | null,
    renderMode?: AppRscRenderMode,
  ) => string;
  isrSet: AppPageCacheSetter;
  middlewareHeaders?: Headers | null;
  middlewareStatus?: number | null;
  mountedSlotsHeader?: string | null;
  renderMode?: AppRscRenderMode;
  expireSeconds?: number;
  revalidateSeconds: number;
  renderFreshPageForCache: () => Promise<AppPageCacheRenderResult>;
  scheduleBackgroundRegeneration: AppPageBackgroundRegenerator;
};

type FinalizeAppPageHtmlCacheResponseOptions = {
  capturedDynamicUsageBeforeContextCleanup?: () => boolean;
  capturedRscDataPromise: Promise<ArrayBuffer> | null;
  cleanPathname: string;
  consumeDynamicUsage: () => boolean;
  getPageTags: () => string[];
  getRequestCacheLife?: () => AppPageRequestCacheLife | null;
  isrDebug?: AppPageDebugLogger;
  isrHtmlKey: (pathname: string) => string;
  isrRscKey: (
    pathname: string,
    mountedSlotsHeader?: string | null,
    renderMode?: AppRscRenderMode,
  ) => string;
  isrSet: AppPageCacheSetter;
  preserveClientResponseHeaders?: boolean;
  expireSeconds?: number;
  revalidateSeconds: number | null;
  waitUntil?: (promise: Promise<void>) => void;
};

type ScheduleAppPageRscCacheWriteOptions = {
  capturedRscDataPromise: Promise<ArrayBuffer> | null;
  cleanPathname: string;
  consumeDynamicUsage: () => boolean;
  dynamicUsedDuringBuild: boolean;
  getPageTags: () => string[];
  getRequestCacheLife?: () => AppPageRequestCacheLife | null;
  isrDebug?: AppPageDebugLogger;
  isrRscKey: (
    pathname: string,
    mountedSlotsHeader?: string | null,
    renderMode?: AppRscRenderMode,
  ) => string;
  isrSet: AppPageCacheSetter;
  mountedSlotsHeader?: string | null;
  renderMode?: AppRscRenderMode;
  preserveClientResponseHeaders?: boolean;
  expireSeconds?: number;
  revalidateSeconds: number | null;
  waitUntil?: (promise: Promise<void>) => void;
};

const NO_STORE_CACHE_CONTROL = "no-store, must-revalidate";

export function buildAppPageCacheTags(pathname: string, extraTags: readonly string[]): string[] {
  const tags = [pathname, `_N_T_${pathname}`, "_N_T_/layout"];
  const segments = pathname.split("/");
  let built = "";
  for (let index = 1; index < segments.length; index++) {
    const segment = segments[index];
    if (segment) {
      built += `/${segment}`;
      tags.push(`_N_T_${built}/layout`);
    }
  }

  tags.push(`_N_T_${built}/page`);
  for (const tag of extraTags) {
    if (!tags.includes(tag)) {
      tags.push(tag);
    }
  }
  // Canonicalise to ASCII-safe form so path-derived tags from non-ASCII
  // pathnames match what `revalidatePath`/`revalidateTag` produce after
  // their own encoding pass.
  return tags.map(encodeCacheTag);
}

function buildAppPageCacheControl(
  cacheState: BuildAppPageCachedResponseOptions["cacheState"],
  revalidateSeconds: number,
  expireSeconds?: number,
): string {
  return buildCachedRevalidateCacheControl(cacheState, revalidateSeconds, expireSeconds);
}

function buildAppPageCachedHeaders(options: {
  cacheControl: string;
  cacheState: BuildAppPageCachedResponseOptions["cacheState"];
  contentType: string;
  middlewareHeaders?: Headers | null;
  mountedSlotsHeader?: string | null;
}): Headers {
  const headers = new Headers({
    "Cache-Control": options.cacheControl,
    "Content-Type": options.contentType,
    Vary: VINEXT_RSC_VARY_HEADER,
    [VINEXT_CACHE_HEADER]: options.cacheState,
  });

  if (options.mountedSlotsHeader) {
    headers.set(VINEXT_MOUNTED_SLOTS_HEADER, options.mountedSlotsHeader);
  }

  mergeMiddlewareResponseHeaders(headers, options.middlewareHeaders ?? null);
  return headers;
}

function getCachedAppPageValue(entry: ISRCacheEntry | null): CachedAppPageValue | null {
  return entry?.value.value && entry.value.value.kind === "APP_PAGE" ? entry.value.value : null;
}

function resolveAppPageCacheWritePolicy(options: {
  expireSeconds?: number;
  requestCacheLife?: AppPageRequestCacheLife | null;
  revalidateSeconds: number | null;
}): { expireSeconds?: number; revalidateSeconds: number } | null {
  let revalidateSeconds = options.revalidateSeconds;
  let expireSeconds = options.expireSeconds;
  const requestCacheLife = options.requestCacheLife;

  if (requestCacheLife?.revalidate !== undefined) {
    revalidateSeconds =
      revalidateSeconds === null
        ? requestCacheLife.revalidate
        : Math.min(revalidateSeconds, requestCacheLife.revalidate);
  }
  if (requestCacheLife?.expire !== undefined) {
    expireSeconds = requestCacheLife.expire;
  }

  if (revalidateSeconds === null || revalidateSeconds <= 0 || !Number.isFinite(revalidateSeconds)) {
    return null;
  }

  return { expireSeconds, revalidateSeconds };
}

export function buildAppPageCachedResponse(
  cachedValue: CachedAppPageValue,
  options: BuildAppPageCachedResponseOptions,
): Response | null {
  // Preserve the legacy fallback semantics from the generated entry: invalid
  // falsy statuses still fall back to 200 rather than being forwarded through.
  const status = options.middlewareStatus ?? (cachedValue.status || 200);
  const revalidateSeconds = options.cacheControl?.revalidate ?? options.revalidateSeconds;
  const expireSeconds =
    options.cacheControl === undefined
      ? undefined
      : (options.cacheControl.expire ?? options.expireSeconds);
  const cacheControl = buildAppPageCacheControl(
    options.cacheState,
    revalidateSeconds,
    expireSeconds,
  );
  if (options.isRscRequest) {
    if (!cachedValue.rscData) {
      return null;
    }

    const rscHeaders = buildAppPageCachedHeaders({
      cacheControl,
      cacheState: options.cacheState,
      contentType: "text/x-component; charset=utf-8",
      middlewareHeaders: options.middlewareHeaders,
      mountedSlotsHeader: options.mountedSlotsHeader,
    });

    return new Response(cachedValue.rscData, {
      status,
      headers: rscHeaders,
    });
  }

  if (typeof cachedValue.html !== "string" || cachedValue.html.length === 0) {
    return null;
  }

  const htmlHeaders = buildAppPageCachedHeaders({
    cacheControl,
    cacheState: options.cacheState,
    contentType: "text/html; charset=utf-8",
    middlewareHeaders: options.middlewareHeaders,
  });

  return new Response(cachedValue.html, {
    status,
    headers: htmlHeaders,
  });
}

export async function readAppPageCacheResponse(
  options: ReadAppPageCacheResponseOptions,
): Promise<Response | null> {
  const isrKey = options.isRscRequest
    ? options.isrRscKey(options.cleanPathname, options.mountedSlotsHeader, options.renderMode)
    : options.isrHtmlKey(options.cleanPathname);

  try {
    const cached = await options.isrGet(isrKey);
    const cachedValue = getCachedAppPageValue(cached);

    if (cachedValue && !cached?.isStale) {
      const hitResponse = buildAppPageCachedResponse(cachedValue, {
        cacheState: "HIT",
        cacheControl: cached?.value.cacheControl,
        expireSeconds: options.expireSeconds,
        isRscRequest: options.isRscRequest,
        middlewareHeaders: options.middlewareHeaders,
        middlewareStatus: options.middlewareStatus,
        mountedSlotsHeader: options.mountedSlotsHeader,
        revalidateSeconds: options.revalidateSeconds,
      });

      if (hitResponse) {
        options.isrDebug?.(
          options.isRscRequest ? "HIT (RSC)" : "HIT (HTML)",
          options.cleanPathname,
        );
        options.clearRequestContext();
        return hitResponse;
      }

      options.isrDebug?.("MISS (empty cached entry)", options.cleanPathname);
    }

    if (cached?.isStale && cachedValue) {
      const regenerationKey = options.isRscRequest
        ? options.isrRscKey(options.cleanPathname, options.mountedSlotsHeader, options.renderMode)
        : options.isrHtmlKey(options.cleanPathname);

      // Preserve the legacy behavior from the inline generator: stale entries
      // still trigger background regeneration even if this request cannot use
      // the stale payload and will fall through to a fresh render.
      options.scheduleBackgroundRegeneration(regenerationKey, async () => {
        const revalidatedPage = await options.renderFreshPageForCache();
        const revalidateSeconds =
          revalidatedPage.cacheControl?.revalidate ?? options.revalidateSeconds;
        const expireSeconds = revalidatedPage.cacheControl?.expire ?? options.expireSeconds;
        const writes = [
          options.isrSet(
            options.isrRscKey(
              options.cleanPathname,
              options.mountedSlotsHeader,
              options.renderMode,
            ),
            buildAppPageCacheValue("", revalidatedPage.rscData, 200),
            revalidateSeconds,
            revalidatedPage.tags,
            expireSeconds,
          ),
        ];

        if (!options.isRscRequest) {
          // HTML cache is slot-state-independent (canonical), so only refresh it
          // during HTML-triggered regens. RSC-triggered regens only update the
          // requesting client's RSC slot variant; a stale HTML cache entry will
          // be regenerated independently by the next full-page HTML request.
          writes.push(
            options.isrSet(
              options.isrHtmlKey(options.cleanPathname),
              buildAppPageCacheValue(revalidatedPage.html, undefined, 200),
              revalidateSeconds,
              revalidatedPage.tags,
              expireSeconds,
            ),
          );
        }

        await Promise.all(writes);
        options.isrDebug?.("regen complete", options.cleanPathname);
      });

      const staleResponse = buildAppPageCachedResponse(cachedValue, {
        cacheState: "STALE",
        cacheControl: cached.value.cacheControl,
        expireSeconds: options.expireSeconds,
        isRscRequest: options.isRscRequest,
        middlewareHeaders: options.middlewareHeaders,
        middlewareStatus: options.middlewareStatus,
        mountedSlotsHeader: options.mountedSlotsHeader,
        revalidateSeconds: options.revalidateSeconds,
      });

      if (staleResponse) {
        options.isrDebug?.(
          options.isRscRequest ? "STALE (RSC)" : "STALE (HTML)",
          options.cleanPathname,
        );
        options.clearRequestContext();
        return staleResponse;
      }

      options.isrDebug?.("STALE MISS (empty stale entry)", options.cleanPathname);
    }

    if (!cached) {
      options.isrDebug?.("MISS (no cache entry)", options.cleanPathname);
    }
  } catch (isrReadError) {
    console.error("[vinext] ISR cache read error:", isrReadError);
  }

  return null;
}

export function finalizeAppPageHtmlCacheResponse(
  response: Response,
  options: FinalizeAppPageHtmlCacheResponseOptions,
): Response {
  if (!response.body) {
    return response;
  }

  const [streamForClient, streamForCache] = response.body.tee();
  const htmlKey = options.isrHtmlKey(options.cleanPathname);
  const rscKey = options.isrRscKey(options.cleanPathname, null);
  const clientHeaders = new Headers(response.headers);
  if (options.preserveClientResponseHeaders !== true) {
    // HTML Server Components can access request APIs while the stream is being
    // consumed. Until that late dynamic check finishes, downstream shared caches
    // must not cache a response whose ISR policy was known before streaming.
    clientHeaders.set("Cache-Control", NO_STORE_CACHE_CONTROL);
    clientHeaders.set(VINEXT_CACHE_HEADER, "MISS");
  }

  const cachePromise = (async () => {
    try {
      const cachedHtml = await readStreamAsText(streamForCache);

      if (
        options.capturedDynamicUsageBeforeContextCleanup?.() === true ||
        options.consumeDynamicUsage()
      ) {
        options.isrDebug?.("HTML cache write skipped (dynamic usage during render)", htmlKey);
        return;
      }

      const cachePolicy = resolveAppPageCacheWritePolicy({
        expireSeconds: options.expireSeconds,
        requestCacheLife: options.getRequestCacheLife?.(),
        revalidateSeconds: options.revalidateSeconds,
      });
      if (!cachePolicy) {
        options.isrDebug?.("HTML cache write skipped (no cache policy)", htmlKey);
        return;
      }

      const pageTags = options.getPageTags();
      const writes = [
        options.isrSet(
          htmlKey,
          buildAppPageCacheValue(cachedHtml, undefined, 200),
          cachePolicy.revalidateSeconds,
          pageTags,
          cachePolicy.expireSeconds,
        ),
      ];

      if (options.capturedRscDataPromise) {
        writes.push(
          options.capturedRscDataPromise.then((rscData) =>
            options.isrSet(
              rscKey,
              buildAppPageCacheValue("", rscData, 200),
              cachePolicy.revalidateSeconds,
              pageTags,
              cachePolicy.expireSeconds,
            ),
          ),
        );
      }

      await Promise.all(writes);
      options.isrDebug?.("HTML cache written", htmlKey);
    } catch (cacheError) {
      console.error("[vinext] ISR cache write error:", cacheError);
    }
  })();

  options.waitUntil?.(cachePromise);

  return new Response(streamForClient, {
    status: response.status,
    statusText: response.statusText,
    headers: clientHeaders,
  });
}

export function finalizeAppPageRscCacheResponse(
  response: Response,
  options: ScheduleAppPageRscCacheWriteOptions,
): Response {
  const didSchedule = scheduleAppPageRscCacheWrite(options);
  if (!didSchedule) {
    return response;
  }

  if (options.preserveClientResponseHeaders === true) {
    return response;
  }

  const clientHeaders = new Headers(response.headers);
  // RSC payloads are also streamed lazily. Until the captured stream proves no
  // late request API was used, the client-facing MISS response must not enter a
  // shared cache when the ISR policy was known before streaming.
  clientHeaders.set("Cache-Control", NO_STORE_CACHE_CONTROL);
  clientHeaders.set(VINEXT_CACHE_HEADER, "MISS");

  return new Response(response.body, {
    status: response.status,
    statusText: response.statusText,
    headers: clientHeaders,
  });
}

export function scheduleAppPageRscCacheWrite(
  options: ScheduleAppPageRscCacheWriteOptions,
): boolean {
  const capturedRscDataPromise = options.capturedRscDataPromise;
  if (!capturedRscDataPromise || options.dynamicUsedDuringBuild) {
    return false;
  }

  const rscKey = options.isrRscKey(
    options.cleanPathname,
    options.mountedSlotsHeader,
    options.renderMode,
  );
  const cachePromise = (async () => {
    try {
      const rscData = await capturedRscDataPromise;

      // Two-phase dynamic detection:
      // 1. dynamicUsedDuringBuild catches searchParams-driven opt-in before the
      //    RSC response is sent.
      // 2. consumeDynamicUsage() here catches APIs that fire while the RSC
      //    stream is consumed (headers(), cookies(), noStore()).
      if (options.consumeDynamicUsage()) {
        options.isrDebug?.("RSC cache write skipped (dynamic usage during render)", rscKey);
        return;
      }

      const cachePolicy = resolveAppPageCacheWritePolicy({
        expireSeconds: options.expireSeconds,
        requestCacheLife: options.getRequestCacheLife?.(),
        revalidateSeconds: options.revalidateSeconds,
      });
      if (!cachePolicy) {
        options.isrDebug?.("RSC cache write skipped (no cache policy)", rscKey);
        return;
      }

      await options.isrSet(
        rscKey,
        buildAppPageCacheValue("", rscData, 200),
        cachePolicy.revalidateSeconds,
        options.getPageTags(),
        cachePolicy.expireSeconds,
      );
      options.isrDebug?.("RSC cache written", rscKey);
    } catch (cacheError) {
      console.error("[vinext] ISR RSC cache write error:", cacheError);
    }
  })();

  options.waitUntil?.(cachePromise);
  return true;
}
